import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography
} from '@mui/material';
import { ReactTable } from '@components';
import {
    MRT_ShowHideColumnsButton,
    MRT_ToggleDensePaddingButton,
    MRT_ToggleFullScreenButton,
    MRT_ToggleGlobalFilterButton
} from 'material-react-table';
import EexTradeModal from './EexTradeModal';
import EexInsertOrderModal from './EexInsertOrderModal';
import TestBidAskModal from './TestBidAskModal';

// const EEX_WS_URL = `wss://${window.location.host}/eexapi/ws/prices`;
const LOCAL_EEX_WS_URL = process.env.REACT_APP_EEX_WS_URL;

const EEX_WS_URL =
    window.location.hostname === 'localhost'
        ? LOCAL_EEX_WS_URL
        : `wss://${window.location.host}/eexapi/ws/prices`;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 5000;

const EVENT_TYPE_GROUPED_LIVE_ORDERS = 'grouped_live_orders';

const getNumericValue = (value, fallback = 0) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const sortEventsByCreatedAt = (events = []) => {
    const safeEvents = Array.isArray(events) ? [...events] : [];

    // If none of the events contain a CreatedAt or BroadcastTimestamp,
    // preserve the original order to avoid introducing spurious reordering
    // (some feeds provide empty timestamps). This prevents rows from being
    // misaligned with the incoming sequence of prices.
    const hasTimestamps = safeEvents.some(
        (e) => !!(e?.CreatedAt || e?.BroadcastTimestamp)
    );

    if (!hasTimestamps) {
        return safeEvents; // keep original order
    }

    safeEvents.sort((a, b) => {
        const aTime =
            Date.parse(a?.CreatedAt || a?.BroadcastTimestamp || '') || 0;
        const bTime =
            Date.parse(b?.CreatedAt || b?.BroadcastTimestamp || '') || 0;
        return aTime - bTime;
    });

    return safeEvents;
};

const formatNumber = (value, digits = 3) =>
    value != null && Number.isFinite(value) ? value.toFixed(digits) : '—';

const normalizeSnapshotToRows = (snapshot) => {
    // Normalize the new format where each product object contains
    // { bids: [...], asks: [...] } and produce depth rows with
    // fields for PID, Bid/Ask prices and quantities.
    const ordersByContract = snapshot?.orders_by_contract;

    if (!ordersByContract || typeof ordersByContract !== 'object') {
        return { rows: [], products: [] };
    }

    // Preserve the incoming product order (do not sort), but prefer a
    // specific product ordering for columns: TTF, THE, PEG. Any other
    // products will follow in the order they were encountered.
    const productSet = new Set();
    Object.values(ordersByContract).forEach((productsMap) => {
        Object.keys(productsMap || {}).forEach((productName) => {
            productSet.add(productName);
        });
    });

    const encountered = Array.from(productSet);
    const preferredOrder = ['TTF', 'THE', 'PEG'];
    const products = [
        ...preferredOrder.filter((p) => encountered.includes(p)),
        ...encountered.filter((p) => !preferredOrder.includes(p))
    ];
    const rows = [];

    // For each contract, create rows indexed by depth level. For each
    // product we pair bids (sorted desc) and asks (sorted asc) by index.
    Object.entries(ordersByContract).forEach(([contractName, productsMap]) => {
        const perProductDepth = {};
        let maxDepth = 0;

        products.forEach((productName) => {
            const productObj = productsMap?.[productName] || {};
            const rawBids = Array.isArray(productObj?.bids)
                ? [...productObj.bids]
                : [];
            const rawAsks = Array.isArray(productObj?.asks)
                ? [...productObj.asks]
                : [];

            // Sort bids descending by price (best bid first)
            rawBids.sort(
                (a, b) =>
                    getNumericValue(b?.Price, 0) - getNumericValue(a?.Price, 0)
            );
            // Sort asks ascending by price (best ask first)
            rawAsks.sort(
                (a, b) =>
                    getNumericValue(a?.Price, 0) - getNumericValue(b?.Price, 0)
            );

            perProductDepth[productName] = { bids: rawBids, asks: rawAsks };
            maxDepth = Math.max(maxDepth, rawBids.length, rawAsks.length);
        });

        for (let depth = 0; depth < maxDepth; depth += 1) {
            const row = { Contract: contractName };

            products.forEach((productName) => {
                const { bids, asks } = perProductDepth[productName] || {
                    bids: [],
                    asks: []
                };
                const bid = bids[depth] || null;
                const ask = asks[depth] || null;

                row[productName] = {
                    // Prefer PersistentOrderId from the element as PID, fallback to productName
                    OrderId:
                        bid?.PersistentOrderId || ask?.PersistentOrderId || '-',
                    BidOrderId: bid?.PersistentOrderId || bid?.OrderId || '',
                    AskOrderId: ask?.PersistentOrderId || ask?.OrderId || '',
                    // Raw order fields for API calls
                    BidPersistentOrderId: bid?.PersistentOrderId || '',
                    AskPersistentOrderId: ask?.PersistentOrderId || '',
                    BidRawOrderId: bid?.OrderId || '',
                    AskRawOrderId: ask?.OrderId || '',
                    EngineId: bid?.EngineId || ask?.EngineId || '',
                    BrokerId: bid?.BrokerId ?? ask?.BrokerId ?? 0,
                    FirstSequenceId:
                        bid?.FirstSequenceId ?? ask?.FirstSequenceId ?? 0,
                    InstName: bid?.InstName || ask?.InstName || '',
                    RouteId: bid?.RouteId ?? ask?.RouteId ?? 0,
                    // Keep raw bid/ask references for deal lookups
                    _rawBid: bid || null,
                    _rawAsk: ask || null,
                    EventCount: bid || ask ? depth + 1 : 0,
                    // Treat left Qty as BidQty, right Qty as AskQty
                    BidQty: bid
                        ? getNumericValue(bid?.Volume ?? bid?.Quantity, 0)
                        : 0,
                    AskQty: ask
                        ? getNumericValue(ask?.Volume ?? ask?.Quantity, 0)
                        : 0,
                    Bid: bid ? getNumericValue(bid?.Price, null) : null,
                    Ask: ask ? getNumericValue(ask?.Price, null) : null,
                    AllOrNone: !!(bid?.AllOrNone || ask?.AllOrNone),
                    Private: !!(
                        bid?.ManualOrderIndicator || ask?.ManualOrderIndicator
                    ),
                    Status: bid?.Status || ask?.Status || '',
                    ExpiryTime: bid?.ExpiryTime || ask?.ExpiryTime || '',
                    PriceDelta: getNumericValue(
                        bid?.PriceDelta ?? ask?.PriceDelta,
                        0
                    ),
                    Discretionary: getNumericValue(
                        bid?.Discretionary ?? ask?.Discretionary,
                        0
                    ),
                    UpdatedAt:
                        bid?.CreatedAt ||
                        ask?.CreatedAt ||
                        bid?.BroadcastTimestamp ||
                        ask?.BroadcastTimestamp ||
                        ''
                };
            });

            rows.push(row);
        }
    });

    return { rows, products };
};

export default function Eex() {
    const [connectionState, setConnectionState] = useState('connecting');
    const [error, setError] = useState('');
    const [rows, setRows] = useState([]);
    const [products, setProducts] = useState([]);
    const [eventType, setEventType] = useState('');
    const [broadcastTimestamp, setBroadcastTimestamp] = useState('');
    const [lastMessageAt, setLastMessageAt] = useState(null);
    const [bidModalOpen, setBidModalOpen] = useState(false);
    const [bidModalInitialTab, setBidModalInitialTab] = useState(0);
    const [bidModalEntry, setBidModalEntry] = useState(null);
    const [askModalOpen, setAskModalOpen] = useState(false);
    const [askModalInitialTab, setAskModalInitialTab] = useState(0);
    const [askModalEntry, setAskModalEntry] = useState(null);

    // Test bid/ask modal state
    const [testBidModalOpen, setTestBidModalOpen] = useState(false);
    const [testAskModalOpen, setTestAskModalOpen] = useState(false);

    // Insert new order modal state
    const [insertOrderModalOpen, setInsertOrderModalOpen] = useState(false);
    const [insertOrderModalEntry, setInsertOrderModalEntry] = useState(null);

    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
    const shouldReconnectRef = useRef(true);

    useEffect(() => {
        shouldReconnectRef.current = true;

        const clearReconnectTimeout = () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        const closeSocket = () => {
            if (wsRef.current) {
                wsRef.current.onclose = null; // Unbind to prevent recursive onclose triggers
                wsRef.current.onerror = null;
                wsRef.current.close();
                wsRef.current = null;
            }
        };

        const scheduleReconnect = () => {
            // clearing existing data before reconnecting can help ensure we don't show stale data if the feed has gone silent
            setRows([]);
            setProducts([]);
            if (!shouldReconnectRef.current) return;

            setConnectionState('reconnecting');
            clearReconnectTimeout();

            const delay = reconnectDelayRef.current;
            reconnectTimeoutRef.current = setTimeout(() => {
                connect();
            }, delay);

            reconnectDelayRef.current = Math.min(
                reconnectDelayRef.current * 2,
                MAX_RECONNECT_DELAY_MS
            );
        };

        const connect = () => {
            clearReconnectTimeout();
            closeSocket();
            setError('');
            setConnectionState((prev) =>
                prev === 'reconnecting' ? 'reconnecting' : 'connecting'
            );

            try {
                const ws = new WebSocket(EEX_WS_URL);

                ws.onopen = () => {
                    setConnectionState('connected');
                    reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
                    setError('');
                };

                ws.onmessage = (event) => {
                    const now = new Date();
                    setLastMessageAt(now);

                    let parsed;
                    try {
                        parsed = JSON.parse(event.data);
                    } catch {
                        return;
                    }

                    if (
                        (parsed?.event_type || '').toString() !==
                        EVENT_TYPE_GROUPED_LIVE_ORDERS
                    ) {
                        return;
                    }

                    const { rows: nextRows, products: nextProducts } =
                        normalizeSnapshotToRows(parsed);

                    setRows(nextRows);
                    setProducts(nextProducts);
                    setEventType((parsed?.event_type || '').toString());
                    setBroadcastTimestamp(
                        (parsed?.broadcast_timestamp || '').toString()
                    );
                };

                ws.onerror = () => {
                    setError(
                        'WebSocket error occurred while streaming EEX prices.'
                    );
                };

                ws.onclose = () => {
                    setConnectionState('closed');
                    if (shouldReconnectRef.current) {
                        scheduleReconnect();
                    }
                };

                wsRef.current = ws;
            } catch {
                setError(
                    'Unable to create WebSocket connection for EEX prices.'
                );
                scheduleReconnect();
            }
        };

        connect();

        return () => {
            shouldReconnectRef.current = false;
            clearReconnectTimeout();
            closeSocket();
        };
    }, []);

    const handleClearMessages = () => {
        setRows([]);
        setProducts([]);
        setEventType('');
        setBroadcastTimestamp('');
    };

    const getDefaultTradeEntry = () => {
        const firstRow = rows[0];
        const firstProduct = products[0];

        if (!firstRow || !firstProduct || !firstRow[firstProduct]) {
            return null;
        }

        return {
            row: firstRow,
            productName: firstProduct
        };
    };

    const handleOpenBidModal = (entry, tabIndex = 0) => {
        if (!entry) return;

        setBidModalEntry({ ...entry, side: 'bid' });
        setBidModalInitialTab(tabIndex);
        setBidModalOpen(true);
    };

    const handleCloseBidModal = () => {
        setBidModalOpen(false);
    };

    const handleOpenAskModal = (entry, tabIndex = 0) => {
        if (!entry) return;

        setAskModalEntry({ ...entry, side: 'ask' });
        setAskModalInitialTab(tabIndex);
        setAskModalOpen(true);
    };

    const handleCloseAskModal = () => {
        setAskModalOpen(false);
    };

    const handleOpenTestBidModal = () => {
        setTestBidModalOpen(true);
    };

    const handleCloseTestBidModal = () => {
        setTestBidModalOpen(false);
    };

    const handleOpenTestAskModal = () => {
        setTestAskModalOpen(true);
    };

    const handleCloseTestAskModal = () => {
        setTestAskModalOpen(false);
    };

    const handleOpenInsertOrderModal = (entry) => {
        const resolvedEntry = entry || getDefaultTradeEntry();
        if (!resolvedEntry) return;
        setInsertOrderModalEntry(resolvedEntry);
        setInsertOrderModalOpen(true);
    };

    const handleCloseInsertOrderModal = () => {
        setInsertOrderModalOpen(false);
    };

    // Called by EexTradeModal after a clean update_order success.
    // Optimistically patches the matching row in local state so the table
    // reflects the new price/qty immediately, before the next WS snapshot.
    const handleOrderUpdated = (tradeEntry, payload) => {
        const productName = tradeEntry?.productName;
        const contractName = tradeEntry?.row?.Contract;

        if (!productName || !contractName) {
            console.warn(
                '[EEX] handleOrderUpdated: missing productName or contractName'
            );
            return;
        }

        const isBid = payload.side === 'BID';

        setRows((prevRows) => {
            const updated = prevRows.map((row) => {
                if (row.Contract !== contractName) return row;

                const productData = row[productName];
                if (!productData) return row;

                const patch = isBid
                    ? {
                          Bid: payload.price,
                          BidQty: payload.volume,
                          AllOrNone: payload.all_or_none
                      }
                    : {
                          Ask: payload.price,
                          AskQty: payload.volume,
                          AllOrNone: payload.all_or_none
                      };

                return { ...row, [productName]: { ...productData, ...patch } };
            });

            return updated;
        });
    };

    const handleOpenBidFromToolbar = () => {
        const entry = getDefaultTradeEntry();
        if (entry) {
            handleOpenBidModal(entry, 0);
        }
    };

    const handleOpenAskFromToolbar = () => {
        const entry = getDefaultTradeEntry();
        if (entry) {
            handleOpenAskModal(entry, 0);
        }
    };

    const columns = useMemo(() => {
        const product = (p) => ({
            header: p,
            id: p,
            muiTableHeadCellProps: {
                sx: {
                    textAlign: 'center',
                    borderRight:
                        p === 'THE' || p === 'TTF' || p === 'PEG'
                            ? '2px solid #3DC3C1'
                            : 'none',
                    pr: 0.5
                }
            },
            columns: [
                {
                    header: 'PID',
                    id: `${p}_PID`,
                    accessorFn: (row) => row[p]?.OrderId || p,
                    size: 60,
                    Cell: ({ cell }) => (
                        <Box sx={{ textAlign: 'center', fontSize: '14px' }}>
                            {cell.getValue()}
                        </Box>
                    )
                },
                {
                    header: 'Qty',
                    id: `${p}_BidQty`,
                    accessorFn: (row) => row[p]?.BidQty || 0,
                    size: 60,
                    Cell: ({ cell }) => (
                        <Box sx={{ textAlign: 'center', fontSize: '14px' }}>
                            {cell.getValue()}
                        </Box>
                    )
                },
                {
                    header: 'Bid',
                    id: `${p}_Bid`,
                    accessorFn: (row) => formatNumber(row[p]?.Bid),
                    size: 60,

                    Cell: ({ cell, row }) => (
                        <Box sx={{ textAlign: 'center', fontSize: '14px' }}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenBidModal(
                                        {
                                            row: row.original,
                                            productName: p
                                        },
                                        0
                                    );
                                }}
                                sx={{
                                    minWidth: 0,
                                    px: 0.5,
                                    py: 0,
                                    my: 0,
                                    textTransform: 'none',
                                    backgroundColor: '#e8f5ff'
                                }}
                            >
                                {cell.getValue()}
                            </Button>
                        </Box>
                    )
                },
                {
                    header: 'Ask',
                    id: `${p}_Ask`,
                    accessorFn: (row) => formatNumber(row[p]?.Ask),
                    size: 60,
                    Cell: ({ cell, row }) => (
                        <Box sx={{ textAlign: 'center', fontSize: '14px' }}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenAskModal(
                                        {
                                            row: row.original,
                                            productName: p
                                        },
                                        0
                                    );
                                }}
                                sx={{
                                    minWidth: 0,
                                    px: 0.5,
                                    py: 0,
                                    my: 0,
                                    textTransform: 'none',
                                    backgroundColor: '#fff3e0'
                                }}
                            >
                                {cell.getValue()}
                            </Button>
                        </Box>
                    )
                },
                {
                    header: 'Qty',
                    id: `${p}_AskQty`,
                    accessorFn: (row) => row[p]?.AskQty || 0,
                    size: 60,
                    muiTableBodyCellProps: {
                        sx: {
                            textAlign: 'center',
                            borderRight:
                                p === 'THE' || p === 'TTF' || p === 'PEG'
                                    ? '2px solid red'
                                    : 'none'
                        }
                    },
                    Cell: ({ cell }) => (
                        <Box sx={{ textAlign: 'center' }}>
                            {cell.getValue()}
                        </Box>
                    )
                }
            ]
        });

        return [
            {
                header: 'Contract',
                id: 'Contract',
                accessorFn: (row) => row.Contract,
                size: 60,
                muiTableHeadCellProps: {
                    sx: {
                        textAlign: 'left',
                        borderRight: '2px solid #3DC3C1',
                        pl: 1
                    }
                },
                muiTableBodyCellProps: {
                    sx: {
                        textAlign: 'left',
                        borderRight: '2px solid #3DC3C1',
                        pl: 1
                    }
                }
            },
            ...products.map((p) => product(p))
        ];
    }, [products]);

    return (
        <Box sx={{ width: '100%', p: 0 }}>
            <EexTradeModal
                open={bidModalOpen}
                initialTab={bidModalInitialTab}
                tradeEntry={bidModalEntry}
                onClose={handleCloseBidModal}
                side="bid"
                rows={rows}
                products={products}
                onOrderUpdated={handleOrderUpdated}
            />
            <EexTradeModal
                open={askModalOpen}
                initialTab={askModalInitialTab}
                tradeEntry={askModalEntry}
                onClose={handleCloseAskModal}
                side="ask"
                rows={rows}
                products={products}
                onOrderUpdated={handleOrderUpdated}
            />
            <TestBidAskModal
                open={testBidModalOpen}
                side="bid"
                onClose={handleCloseTestBidModal}
            />
            <TestBidAskModal
                open={testAskModalOpen}
                side="ask"
                onClose={handleCloseTestAskModal}
            />
            <EexInsertOrderModal
                open={insertOrderModalOpen}
                tradeEntry={insertOrderModalEntry}
                onClose={handleCloseInsertOrderModal}
            />
            <Stack spacing={0.5}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap'
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        {(connectionState === 'connecting' ||
                            connectionState === 'reconnecting') && (
                            <CircularProgress size={18} />
                        )}
                        <Typography variant="body1">
                            Status:{' '}
                            <strong>
                                {connectionState === 'connected'
                                    ? 'Connected'
                                    : connectionState === 'reconnecting'
                                      ? 'Reconnecting'
                                      : connectionState === 'closed'
                                        ? 'Disconnected'
                                        : 'Connecting'}
                            </strong>
                        </Typography>
                    </Stack>

                    {/* <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={handleOpenTestBidModal}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#29a000',
                                color: '#29a000',
                                fontSize: '12px',
                                '&:hover': {
                                    borderColor: '#29a000',
                                    bgcolor: '#e8f5e9'
                                }
                            }}
                        >
                            🧪 Test Bid
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={handleOpenTestAskModal}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#ff6d00',
                                color: '#ff6d00',
                                fontSize: '12px',
                                '&:hover': {
                                    borderColor: '#ff6d00',
                                    bgcolor: '#fff3e0'
                                }
                            }}
                        >
                            🧪 Test Ask
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            Last update:{' '}
                            {lastMessageAt
                                ? lastMessageAt.toLocaleString()
                                : 'No data yet'}
                        </Typography>
                    </Stack> */}
                </Box>

                {error && <Alert severity="error">{error}</Alert>}

                {connectionState !== 'connected' && rows.length === 0 && (
                    <Alert severity="info">
                        Waiting for EEX stream data. The page will
                        auto-reconnect if the socket closes.
                    </Alert>
                )}
                <Box sx={{ mt: 0.5 }}>
                    <ReactTable
                        headerText="Live EEX Orders"
                        columns={columns}
                        data={rows}
                        isLoading={connectionState === 'connecting'}
                        showCellBorders={true}
                        renderToolbarInternalActions={({ table }) => (
                            <Box
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleOpenInsertOrderModal()}
                                    sx={{
                                        textTransform: 'none',
                                        bgcolor: '#245372',
                                        color: '#fff',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        '&:hover': { bgcolor: '#1a3d54' }
                                    }}
                                >
                                    + New Order
                                </Button>
                                <MRT_ToggleGlobalFilterButton table={table} />
                                <MRT_ShowHideColumnsButton table={table} />
                                <MRT_ToggleDensePaddingButton table={table} />
                                <MRT_ToggleFullScreenButton table={table} />
                            </Box>
                        )}
                        muiTableContainerProps={{
                            sx: {
                                maxHeight: 'calc(100vh - 220px)',
                                overflow: 'auto'
                            }
                        }}
                        muiTableHeadCellProps={{
                            sx: {
                                py: 0,
                                px: 0.75,
                                fontSize: '12px'
                            }
                        }}
                        muiTableBodyCellProps={{
                            sx: {
                                py: 0,
                                px: 0.75,
                                fontSize: '12px'
                            }
                        }}
                        // density="spacious"
                    />
                </Box>

                {rows.length === 0 && connectionState === 'connected' && (
                    <Alert severity="info">No orders received yet.</Alert>
                )}
            </Stack>
        </Box>
    );
}
